import { useState } from "react";
import { ArrowUpRight, FileBarChart, Plus, Trash2 } from "lucide-react";
import {
  useCreate,
  useDelete,
  useGetList,
  useLocaleState,
  useNotify,
  useRefresh,
  useTranslate,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSignedUrl } from "../calendar/useSignedUrl";
import type { ClientReport, ReportKind } from "../types";
import {
  CLIENT_FILES_BUCKET,
  MAX_CLIENT_FILE_BYTES,
  removeClientFile,
  uploadClientFile,
} from "./clientFiles";

const formatPeriod = (period: string, locale: string): string => {
  const [year, month] = period.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString(
    locale === "es" ? "es-CO" : locale,
    { month: "long", year: "numeric" },
  );
};

// Who manages the list: administrators publish any area, the team only
// content reports (enforced by RLS too). Undefined means read-only (clients).
export type ReportManager = "admin" | "team";

const REPORT_KINDS: ReportKind[] = ["content", "paid_media", "web", "general"];

export const ClientReports = ({
  companyId,
  manager,
}: {
  companyId: Identifier;
  manager?: ReportManager;
}) => {
  const translate = useTranslate();
  const [isAdding, setIsAdding] = useState(false);
  const { data: reports = [], isPending } = useGetList<ClientReport>(
    "client_reports",
    {
      filter: { company_id: companyId },
      sort: { field: "period", order: "DESC" },
      pagination: { page: 1, perPage: 120 },
    },
  );

  return (
    <div className="flex flex-col gap-3">
      {manager ? (
        isAdding ? (
          <ReportForm
            companyId={companyId}
            manager={manager}
            onDone={() => setIsAdding(false)}
          />
        ) : (
          <Button
            variant="outline"
            className="self-start rounded-full"
            onClick={() => setIsAdding(true)}
          >
            <Plus className="size-4" />
            {translate("resources.client_reports.add")}
          </Button>
        )
      ) : null}
      {!isPending && reports.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.client_reports.empty")}
        </p>
      ) : null}
      <ul className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
        {reports.map((report) => (
          <li key={report.id}>
            <ReportCard
              report={report}
              canDelete={
                manager === "admin" ||
                (manager === "team" && report.kind === "content")
              }
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

const ReportCard = ({
  report,
  canDelete,
}: {
  report: ClientReport;
  canDelete: boolean;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const [deleteOne] = useDelete<ClientReport>();
  const refresh = useRefresh();
  const signedUrl = useSignedUrl(report.file_path, CLIENT_FILES_BUCKET);
  const href = report.external_url ?? signedUrl ?? undefined;

  const handleDelete = async () => {
    if (!window.confirm(translate("resources.client_reports.delete_confirm"))) {
      return;
    }
    if (report.file_path) await removeClientFile(report.file_path);
    deleteOne(
      "client_reports",
      { id: report.id, previousData: report },
      { mutationMode: "pessimistic", onSuccess: () => refresh() },
    );
  };

  return (
    <div className="group relative flex h-full flex-col gap-3 rounded-[22px] bg-secondary p-5">
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={translate("resources.client_reports.open")}
        className="absolute -top-1.5 -right-1.5 grid size-11 place-items-center rounded-full border-[6px] border-card bg-primary text-primary-foreground"
      >
        <ArrowUpRight className="size-4" />
      </a>
      <span className="flex items-center gap-2 text-xs text-muted-foreground">
        <FileBarChart className="size-4" />
        {translate(`resources.client_reports.kinds.${report.kind}`)}
      </span>
      <span className="text-lg font-semibold first-letter:uppercase">
        {formatPeriod(report.period, locale)}
      </span>
      {report.summary ? (
        <p className="text-sm leading-relaxed text-foreground/80">
          {report.summary}
        </p>
      ) : null}
      {canDelete ? (
        <button
          type="button"
          onClick={handleDelete}
          aria-label="delete"
          className="mt-auto self-end text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
        >
          <Trash2 className="size-4" />
        </button>
      ) : null}
    </div>
  );
};

const ReportForm = ({
  companyId,
  manager,
  onDone,
}: {
  companyId: Identifier;
  manager: ReportManager;
  onDone: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate<ClientReport>();
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  const [month, setMonth] = useState(
    `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, "0")}`,
  );
  const [summary, setSummary] = useState("");
  const [kind, setKind] = useState<ReportKind>("content");
  const [file, setFile] = useState<File | null>(null);
  const [link, setLink] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file && !link.trim()) {
      setError(translate("resources.client_reports.need_file_or_link"));
      return;
    }
    if (file && file.size > MAX_CLIENT_FILE_BYTES) {
      setError(translate("resources.content_pieces.versions.too_large"));
      return;
    }
    setIsSaving(true);
    try {
      const filePath = file
        ? await uploadClientFile(companyId, "reports", file)
        : null;
      create(
        "client_reports",
        {
          data: {
            company_id: companyId,
            period: `${month}-01`,
            kind: manager === "admin" ? kind : "content",
            summary: summary.trim() || null,
            file_path: filePath,
            external_url: link.trim() || null,
          },
        },
        {
          onSuccess: () => {
            refresh();
            notify("resources.client_reports.saved", { type: "success" });
            onDone();
          },
          onError: () =>
            notify("ra.notification.http_error", { type: "error" }),
          onSettled: () => setIsSaving(false),
        },
      );
    } catch {
      setIsSaving(false);
      notify("ra.notification.http_error", { type: "error" });
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 rounded-2xl border border-border p-4"
    >
      {manager === "admin" ? (
        <div className="flex flex-col gap-1.5">
          <Label>{translate("resources.client_reports.kind")}</Label>
          <Select
            value={kind}
            onValueChange={(value) => setKind(value as ReportKind)}
          >
            <SelectTrigger className="sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPORT_KINDS.map((option) => (
                <SelectItem key={option} value={option}>
                  {translate(`resources.client_reports.kinds.${option}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {translate("resources.client_reports.kind")}:{" "}
          {translate("resources.client_reports.kinds.content")}
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-[180px_1fr]">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-month">
            {translate("resources.client_reports.month")}
          </Label>
          <Input
            id="report-month"
            type="month"
            value={month}
            onChange={(event) => setMonth(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="report-summary">
            {translate("resources.client_reports.summary")}
          </Label>
          <Input
            id="report-summary"
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
          />
        </div>
      </div>
      <label className="text-xs text-muted-foreground">
        {translate("resources.client_reports.file")}
        <Input
          type="file"
          accept="application/pdf,image/*"
          className="mt-1"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
            setError(null);
          }}
        />
      </label>
      <Input
        placeholder={translate("resources.client_reports.link")}
        value={link}
        onChange={(event) => {
          setLink(event.target.value);
          setError(null);
        }}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          {translate("ra.action.cancel")}
        </Button>
        <Button type="submit" className="rounded-full" disabled={isSaving}>
          {translate("resources.client_reports.add")}
        </Button>
      </div>
    </form>
  );
};
